import React, {
    Dispatch, useContext, useEffect, useMemo,
} from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { RapidStorSettings } from '@/core/utils/rapidstor';

import store from '@/store';
import {
    ReservationData,
    selectReservationData,
    selectUnitData,
    setReservationData,
    setUnitData,
} from '@/store/bookingSlice';

import { dealHasMonetaryDiscount, getDealDefaultText, getDealPriceDisplay } from '@/core/utils/dealDisplay';
import { getEffectiveDealRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import Inclusions from '@/journey-based-listing/components/descriptor/inclusion';
import DescriptorTag from '@/journey-based-listing/components/descriptor/tag';
import Coupon from '@/journey-based-listing/components/rapidstor/coupon';
import Option from '@/journey-based-listing/components/unit-option';

import {
    ANIMATION_SPEED,
    selectedCardDesktopVariants,
    selectedCardMobileVariants,
} from '@/features/rapidstor/animations';
import {
    descriptorDescriptions,
    descriptorTitle,
    formatDealCustomText,
    formatTimeframe,
    percentageDifference,
} from '@/features/rapidstor/helpers';
import { useAnimationContext } from '@/features/rapidstor/providers/AnimationProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import {
    DescriptorOptions,
    unitOptionsTransformer,
    UpgradeDescriptor,
} from '@/features/rapidstor/UnitOptions';

import baseClasses from '../styles.module.scss';

import CardButtons from '../CardButtons';

interface Props {
    deal: string | null;
    descriptor: Descriptor;
    descriptorOptions: UpgradeDescriptor[];
    unitTypeOptions: UnitType[];
    desktop: boolean;
    reservationToken: string | null;
    selectedID: string;
    loading: boolean;
    setLoading: Dispatch<React.SetStateAction<boolean>>;

    makeReservation: () => void;
    setSelectedID: Dispatch<React.SetStateAction<string>>;
    onOptionSelect?: (option: UnitType, descriptor?: UpgradeDescriptor) => void;
    dealSelectorSlot?: React.ReactNode;
    dealBannerSlot?: React.ReactNode;
    descriptorConfirmSlot?: React.ReactNode;
}

export default function SelectedCard({
    deal,
    descriptor,
    descriptorOptions,
    unitTypeOptions,
    desktop,
    reservationToken,
    selectedID,
    loading,
    setLoading,

    makeReservation,
    setSelectedID,
    onOptionSelect,
    dealSelectorSlot,
    dealBannerSlot,
    descriptorConfirmSlot,
}: Props) {
    const location = useLoaderData() as Location;
    const reservationData = useSelector(selectReservationData);
    const unitData = useSelector(selectUnitData);
    const useDescriptorOptions = DescriptorOptions(descriptor, unitData?.descriptors ?? [], location);
    const { t } = useTranslation();
    const { toggleContactCard } = useContext(JourneyContext);
    const { selectedCardState, setSelectedCardState } = useAnimationContext();

    const options = useDescriptorOptions.isDescriptorUpsell()
        ? descriptorOptions
        : unitTypeOptions;

    const unitType = useDescriptorOptions.getUnitTypeUpsells()[0];

    const classNames = [
        baseClasses.card,
        desktop === true ? baseClasses.desktop : baseClasses.mobile,
        deal && baseClasses.deal,
        baseClasses.primary,
    ].join(' ');

    const selectUpsellCard = () => {
        if (!unitData) {
            return;
        }
        store.dispatch(
            setUnitData({
                location,
                unitType,
                descriptor,
                descriptors: unitData.descriptors,
            }),
        );

        const reservationStoreData: ReservationData = {
            unitType,
            descriptor,
            deal: '',
            coupon: null,
        };

        if (reservationToken) {
            reservationStoreData.token = reservationToken;
        }

        store.dispatch(setReservationData(reservationStoreData));
    };

    const cardVariantsToUse = useMemo(() => {
        if (desktop === true) {
            return selectedCardDesktopVariants;
        }
        return selectedCardMobileVariants;
    }, [desktop]);

    const cardInitialValueToUse = useMemo(() => {
        if (desktop === true) {
            return 'start';
        }
        return 'start';
    }, [desktop]);

    const shouldShowCardButtons = useMemo(() => {
        if (desktop) {
            return useDescriptorOptions.isDescriptorUpsell()
                ? descriptorOptions.length <= 0
                : unitTypeOptions.length <= 0;
        }
        return true;
    }, [descriptorOptions.length, desktop, unitTypeOptions.length, useDescriptorOptions]);

    const percentageDifferenceValue = useMemo(() => {
        const original = reservationData?.unitType.rates.rate ?? 0;
        const discounted = reservationData?.dealObject
            ? reservationData?.dealObject?.rates.discountedRate ?? 0
            : reservationData?.unitType.rates.rate ?? 0;

        return percentageDifference(original, discounted);
    }, [reservationData]);

    const textBelowPrice = useMemo((): React.ReactNode | undefined => {
        if (!deal) return undefined;

        const hasMonetaryDiscount = dealHasMonetaryDiscount(reservationData?.dealObject);

        if (
            hasMonetaryDiscount
            && reservationData?.dealObject?.customFormat.primary
            && reservationData.dealObject.customFormat.primary !== ''
        ) {
            return (
                <div
                    className={`${baseClasses.pricing_desc} ${baseClasses.customText}`}
                >
                    <span>
                        {formatDealCustomText(
                            reservationData?.dealObject,
                            location,
                            reservationData.dealObject.customFormat.primary,
                        )}
                    </span>
                    <span>
                        {formatDealCustomText(
                            reservationData?.dealObject,
                            location,
                            reservationData.dealObject.customFormat.secondary,
                        )}
                    </span>
                </div>
            );
        }

        const defaultText = hasMonetaryDiscount && reservationData?.dealObject
            ? getDealDefaultText(reservationData.dealObject, location)
            : null;

        if (defaultText) {
            return (
                <div className={baseClasses.pricing_desc}>
                    <span>{defaultText}</span>
                </div>
            );
        }

        if (!hasMonetaryDiscount) {
            return (
                <div className={baseClasses.pricing_desc}>
                    <span>{formatTimeframe(location)}</span>
                </div>
            );
        }

        return (
            <div className={baseClasses.pricing_desc}>
                <span>
                    {`${t('unit_card:valued_at')} ${formatPriceLabel(formatCurrency(
                        reservationData?.unitType.rates.rate ?? 0,
                        location,
                    ), location)}`}
                </span>
                <span className={baseClasses.savings_badge}>
                    {percentageDifferenceValue}
                </span>
            </div>
        );
    }, [deal, location, percentageDifferenceValue, reservationData, t]);

    useEffect(() => {
        if (desktop) {
            setSelectedCardState('show');
        } else if (options.length >= 1) {
            setSelectedCardState('show');
        } else {
            setSelectedCardState('close');
        }
    }, [desktop, options.length, setSelectedCardState]);

    return (
        <motion.div
            layout
            variants={cardVariantsToUse}
            initial={cardInitialValueToUse}
            animate={selectedCardState}
            id={`${selectedCardState}`}
            transition={{
                duration: ANIMATION_SPEED,
            }}
            className={`${baseClasses.card_container}`}
        >
            <div className={`${classNames}`}>
                {dealBannerSlot || (reservationData?.dealObject && reservationData?.deal && (
                    <div className={`${baseClasses.deal_banner}`}>
                        <span>{reservationData?.dealObject?.title}</span>
                    </div>
                ))}

                <div className={`${baseClasses.card_body}`}>
                    <div className={`${baseClasses.header}`}>
                        <div className={baseClasses.first}>
                            <span>
                                <label className={`${baseClasses.underline}`}>
                                    {descriptorTitle(descriptor, location)[1]}
                                </label>
                            </span>
                        </div>
                        {options.length <= 1 && (
                            <div className={baseClasses.last}>
                                <span>
                                    {deal && reservationData?.dealObject
                                        ? getDealPriceDisplay(
                                            reservationData.dealObject,
                                            getEffectiveDealRate(reservationData.dealObject, reservationData?.unitType.rates.rate ?? 0),
                                            location,
                                        )
                                        : formatPriceLabel(formatCurrency(reservationData?.unitType.rates.rate ?? 0, location), location)}
                                </span>
                                {textBelowPrice}
                            </div>
                        )}
                    </div>

                    <Inclusions
                        inclusions={
                            deal && reservationData?.dealObject
                                ? reservationData.dealObject.descriptions
                                : descriptorDescriptions(descriptor, false)
                        }
                    />

                    {desktop && (
                        <DescriptorTag
                            tags={reservationData?.unitType.deal ? reservationData?.unitType.deal.tags : descriptor.tags}
                            secondary
                        />
                    )}

                    {options.length >= 1 && reservationData && (
                        <Option
                            option={reservationData.unitType}
                            location={location}
                            dealObject={reservationData.dealObject}
                            default
                            selected={selectedID === reservationData.unitType._id}
                            optionText={descriptor.jblOptionText}
                            setSelectedID={setSelectedID}
                            theme="base"
                        />
                    )}

                    <div className={baseClasses.mobile}>
                        <span className={baseClasses.customise_booking}>
                            {t('unit_card:customise_booking')}
                        </span>

                        {descriptorConfirmSlot}

                        {useDescriptorOptions.isDescriptorUpsell() ? (
                            <>
                                {descriptorOptions.map((option) => unitOptionsTransformer(option)[0] && (
                                    <Option
                                        key={`${option._id}_upsell_options_idx`}
                                        location={location}
                                        option={unitOptionsTransformer(option)[0]}
                                        descriptor={option}
                                        desktopOption={desktop}
                                        initialRate={deal && reservationData?.dealObject ?
                                            getEffectiveDealRate(reservationData?.dealObject, reservationData?.unitType.rates.rate) :
                                            reservationData?.unitType.rates.rate}
                                        selected={
                                            selectedID
                                            === unitOptionsTransformer(option)[0]._id
                                        }
                                        setSelectedID={setSelectedID}
                                        theme="base"
                                        onOptionSelect={onOptionSelect}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                {unitTypeOptions.map((option) => (
                                    <Option
                                        key={`${option._id}_upsell_options_idx`}
                                        location={location}
                                        option={option}
                                        selected={selectedID === option._id}
                                        desktopOption={desktop}
                                        initialRate={deal && reservationData?.dealObject ?
                                            getEffectiveDealRate(reservationData?.dealObject, reservationData?.unitType.rates.rate) :
                                            reservationData?.unitType.rates.rate}
                                        setSelectedID={setSelectedID}
                                        theme="base"
                                        onOptionSelect={onOptionSelect}
                                    />
                                ))}
                            </>
                        )}
                    </div>

                    {dealSelectorSlot}

                    {RapidStorSettings(location).showDealCoupons()
                        && !desktop
                        && options.length >= 1 && <Coupon type="primary" />}

                    {shouldShowCardButtons && (
                        <CardButtons
                            isLoading={loading}
                            cardState={selectedCardState}
                            toggleContactCard={toggleContactCard}
                            makeReservation={makeReservation}
                            selectUpsellCard={selectUpsellCard}
                            setIsLoading={setLoading}
                            desktop={desktop}
                            unitType={unitType}
                            deal={deal}
                            options={unitTypeOptions}
                        />
                    )}
                </div>
            </div>

            {location.rapidstor.useDealCoupons && desktop && <Coupon type="base" />}
        </motion.div>
    );
}
